/**
 * Identity — chunk 5.
 *
 * Contract mirror: labourcore-api/src/modules/identity/.
 *
 * ── The rule this module puts into code ────────────────────────────────────
 *
 *     Ownership is a company. Attribution is a person.
 *
 * Product-KT/09 has the reasoning. What it means here is that a screen asks
 * "what does the company I am acting as own?" and separately "what did I do?",
 * and stops trying to answer either one from a marketplace side stored against
 * a person.
 *
 * ── What starts working ────────────────────────────────────────────────────
 *
 * 1. THE PROJECTS PANEL RETURNS ROWS. It ran
 *
 *        .from("projects").eq("org_id", coordCompanyId)
 *
 *    `org_id` holds a USER id. `coordCompanyId` is a COMPANY id. Both are
 *    uuids, so nothing raised and nothing warned — and no row has ever
 *    matched. That panel has shown its empty state to every user since it was
 *    written. Migration 024 gives projects a `coordinator_company_id` that
 *    means what its name says, and the endpoint reads it.
 *
 * 2. PLACEMENT ROWS HAVE NAMES ON THEM. The read asked for the operative and
 *    the requirement with embed syntax — `operative:operative_id(full_name,
 *    trade)` — which the proxy drops. The values were always undefined and the
 *    markup sat behind an `&&`, so nothing was drawn: not blank, absent.
 *
 * 3. THE RIGHT PANELS APPEAR. Which panels the /me screen showed was decided
 *    by `user.role` — profiles.role, one marketplace side per PERSON. Priya is
 *    a member of a hybrid and owner of a coordinator; there is no single value
 *    that column could hold that would be right for both. The server now
 *    returns `acting_kind` from the company, and the screen is told rather
 *    than guessing.
 *
 * 4. CONSENT IS ACTUALLY RECORDED. The browser's insert named `consent_kind`
 *    and `policy_version` — columns that do not exist on `gdpr_consents`. It
 *    threw on every single signup and the caller swallowed it (#164). No
 *    consent has ever been written. It is one endpoint now, in the same
 *    transaction as the profile it belongs to, and the IP is taken from the
 *    request rather than from the body.
 *
 * ── What is deliberately not sent ──────────────────────────────────────────
 *
 * `role`. Registration wrote it on every signup, which is where profiles.role
 * came from in the first place. Migration 029 drops the column; this module is
 * what stops it being written in the meantime.
 */

import { apiGet, apiPatch, apiPost } from "./client";

export type Row = Record<string, unknown>;

export interface ProfileOverview {
  profile: Row | null;
  company: Row | null;
  /** The acting company's kind — what decides which panels apply. */
  acting_kind: "coordinator" | "partner" | "hybrid" | null;
  placements: Row[];
  my_ratings: Row[];
  company_ratings: Row[];
  operatives: Row[];
  compliance_documents: Row[];
  requirements: Row[];
  projects: Row[];
}

/** The caller's own profile row. Never anybody else's — there is no id here. */
export function fetchOwnProfile(): Promise<Row | null> {
  return apiGet<Row | null>("/api/profile");
}

/**
 * Everything the /me screen renders, in one request.
 *
 * `companyId` is optional: when the acting-context headers are set — which
 * they are for every call the shell makes — the server prefers the company it
 * has already validated against memberships on this request.
 */
export function fetchProfileOverview(companyId?: string | null): Promise<ProfileOverview> {
  return apiGet<ProfileOverview>(
    "/api/profile/overview",
    companyId ? { company_id: companyId } : undefined,
  );
}

export interface ProfilePatch {
  name?: string;
  title?: string;
  location?: string;
  company?: string;
  trades?: string[];
  regions?: string[];
  declared_certs?: string[];
  project_types?: string[];
  portfolio_images?: string[];
  crew_size?: number;
  typical_headcount?: number;
  typical_day_rate?: number;
  procurement_system?: string;
  avatar_url?: string;
  cover_url?: string;
}

/**
 * Update your own profile.
 *
 * The columns are an allowlist server-side. `is_admin`, `admin_role` and
 * `onboarding_complete` live on the same table and are not on it — which is
 * the point of having one.
 */
export function updateOwnProfile(
  patch: ProfilePatch,
): Promise<{ profile: Row; fields_changed: string[] }> {
  return apiPatch<{ profile: Row; fields_changed: string[] }>("/api/profile", patch);
}

export interface BootstrapInput {
  email: string;
  name: string;
  company: string;
  title?: string;
  location?: string;
  /** Omit to record nothing. Omitting is not the same as recording a refusal. */
  consent_scope?: "data_processing" | "marketing" | "analytics";
  trades?: string[];
  regions?: string[];
  declared_certs?: string[];
  project_types?: string[];
  crew_size?: number;
  typical_day_rate?: number;
  typical_headcount?: number;
  procurement_system?: string;
}

/**
 * Finish setting up an account.
 *
 * Returns `company_id` when the caller already has a membership, which is the
 * #86 guard: the setup screen must not create a second company for someone
 * whose membership landed a moment after it rendered.
 */
export function bootstrapAccount(
  input: BootstrapInput,
): Promise<{ profile: Row; company_id: string | null; consent: Row | null }> {
  return apiPost("/api/identity/bootstrap", input);
}

export function recordConsent(
  scope: "data_processing" | "marketing" | "analytics",
  granted = true,
): Promise<Row> {
  return apiPost<Row>("/api/identity/consent", { scope, granted });
}
